import { merge } from 'lodash';
import moment from 'moment-timezone';

/**
 * Optimistically updates objects with the modified data
 * Will merge objects to allow for partial updates
 */
export async function updateQueryCache(queryClient, queryKey, updatedItem) {
    const id = updatedItem._id ?? updatedItem.id;
    await queryClient.cancelQueries({ queryKey });
    const queries = queryClient.getQueryCache().findAll({ queryKey });

    const originalQueries = queries.map((query) => ({
        queryKey: query.queryKey,
        state: { data: query.state.data },
    }));

    queries.forEach((query) => {
        const previousData = query.state.data;

        if (Array.isArray(previousData)) {
            queryClient.setQueryData(
                query.queryKey,
                previousData.map((item) => (item._id === id || item.id === id ? merge(item, updatedItem) : item))
            );
        } else if (previousData && typeof previousData === 'object') {
            queryClient.setQueryData(query.queryKey, merge(previousData, updatedItem));
        }
    });

    return { queries: originalQueries };
}

/**
 * Optimistically updates deleted objects with `deleted_at` property or removes them from the data set
 * Items that update the `deleted_at` property should set the query key `{includeDeleted: true}` inside the key array
 */
export async function setDeletedQueryCache(queryClient, queryKey, updatedItem) {
    const id = updatedItem._id ?? updatedItem.id;
    await queryClient.cancelQueries({ queryKey });
    const queries = queryClient.getQueryCache().findAll({ queryKey });

    const originalQueries = queries.map((query) => ({
        queryKey: query.queryKey,
        state: { data: query.state.data },
    }));

    queries.forEach((query) => {
        const previousData = query.state.data;

        const queryParams = query.queryKey?.[1] || {};
        const { includeDeleted } = queryParams;

        if (Array.isArray(previousData)) {
            if (includeDeleted) {
                queryClient.setQueryData(
                    query.queryKey,
                    previousData.map((item) => (item._id === id || item.id === id ? { ...item, deleted_at: moment().toISOString() } : item))
                );
            } else {
                queryClient.setQueryData(
                    query.queryKey,
                    previousData.filter((item) => !(item._id === id || item.id === id))
                );
            }
        } else if (previousData && typeof previousData === 'object') {
            if (includeDeleted) {
                queryClient.setQueryData(query.queryKey, { ...previousData, deleted_at: moment().toISOString() });
            } else {
                queryClient.setQueryData(query.queryKey, null);
            }
        }
    });

    return { queries: originalQueries };
}

/**
 * Optimistically updates restored objects with `deleted_at` property set to null
 */
export async function restoreDeletedQueryCache(queryClient, queryKey, updatedItem) {
    const id = updatedItem._id ?? updatedItem.id;
    await queryClient.cancelQueries({ queryKey });
    const queries = queryClient.getQueryCache().findAll({ queryKey });

    const originalQueries = queries.map((query) => ({
        queryKey: query.queryKey,
        state: { data: query.state.data },
    }));

    queries.forEach((query) => {
        const previousData = query.state.data;

        if (Array.isArray(previousData)) {
            queryClient.setQueryData(
                query.queryKey,
                previousData.map((item) => (item._id === id || item.id === id ? { ...item, deleted_at: null } : item))
            );
        } else if (previousData && typeof previousData === 'object') {
            queryClient.setQueryData(query.queryKey, updatedItem);
        }
    });

    return { queries: originalQueries };
}

/**
 * Restores original dataset before optimistic update occured
 */
export async function restoreCache(queryClient, ctx) {
    if (ctx?.queries) {
        ctx.queries.forEach((query) => {
            queryClient.setQueryData(query.queryKey, query.state.data);
        });
    }
}
